import { useState } from "react";
import { Box, Palette, Stack, Typography } from "@mui/material";
import { CheckCircle2 } from "lucide-react";
import { IChatBlock } from "@pages/askDost/types";
import {
  mcqContainerSx,
  mcqOptionSx,
  mcqQuestionSx,
  mcqHintSx,
} from "@pages/askDost/styles/McqBlockStyle";

interface IMcqBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onOptionSelect?: (text: string) => void;
}

export const McqBlock: React.FC<IMcqBlockProps> = ({
  block,
  palette,
  onOptionSelect,
}) => {
  const mcq = block?.mcq;
  const [selected, setSelected] = useState<number | null>(null);

  if (!mcq) return null;

  const handleSelect = (index: number, label: string) => {
    if (selected !== null) return;
    setSelected(index);
    if (onOptionSelect) {
      onOptionSelect(label);
    }
  };

  const renderOption = (option: { label: string }, index: number) => {
    const isSelected = selected === index;
    return (
      <Stack
        key={option?.label}
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => handleSelect(index, option?.label)}
        sx={mcqOptionSx(palette, isSelected, selected)}
      >
        {isSelected && (
          <CheckCircle2 size={16} color={palette.secondary.dark} />
        )}
        <Typography variant="body2" sx={{ color: palette.text.primary }}>
          {option?.label}
        </Typography>
      </Stack>
    );
  };

  return (
    <Box sx={mcqContainerSx(palette)}>
      <Typography variant="subtitle2" sx={mcqQuestionSx(palette)}>
        {mcq?.question}
      </Typography>
      <Typography variant="caption" sx={mcqHintSx(palette)}>
        {mcq?.hint}
      </Typography>
      <Stack spacing={1}>
        {mcq?.options?.map((option, index) => renderOption(option, index))}
      </Stack>
    </Box>
  );
};
